import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn, ShieldCheck } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/App";
import { Logo } from "@/components/Logo";

export function TeamLogin() {
  const nav = useNavigate();
  const { setToken, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pms/team-login", { email: email.trim().toLowerCase(), password });
      localStorage.setItem("atiti_token", data.token);
      localStorage.setItem("atiti_user", JSON.stringify(data.user));
      setToken(data.token); setUser(data.user);
      toast.success(`Welcome back, ${data.user.name}`);
      nav("/pms/app");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6"><Logo /></div>
        <form data-testid="team-login-form" onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 space-y-4">
          <div className="flex items-center gap-2 mb-2"><ShieldCheck className="brand-pink" size={18} /><h1 className="font-display font-black text-2xl text-slate-900">Team sign-in</h1></div>
          <p className="text-sm text-slate-500">Managers, reception & staff sign in here. Admins use the main login.</p>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Work email</label>
            <input data-testid="team-login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Password</label>
            <input data-testid="team-login-password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
          <button data-testid="team-login-submit" type="submit" disabled={busy} className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <LogIn size={14} />} Sign in
          </button>
          <div className="flex items-center justify-between pt-1">
            <a href="/team-forgot" data-testid="team-forgot-link" className="text-[11px] font-semibold text-[#4F46E5] hover:underline">Forgot password?</a>
            <span className="text-[11px] text-slate-400">Same login on web &amp; mobile</span>
          </div>
          <p className="text-[11px] text-slate-400 text-center">Access is scoped to properties your admin assigned. Wrong login? Ask the admin to re-send your invite.</p>
        </form>
      </div>
    </div>
  );
}

export function TeamInviteAccept() {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/pms/team-accept-invite", { token, password });
      toast.success(`Password set for ${data.email} — sign in now`);
      nav("/team-login");
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6"><Logo /></div>
        <form data-testid="team-accept-form" onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 space-y-4">
          <h1 className="font-display font-black text-2xl text-slate-900">Set your password</h1>
          <p className="text-sm text-slate-500">Finish activating your Atiti PMS account. This link is single-use and expires in 7 days.</p>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">New password</label>
            <input data-testid="team-accept-password" type="password" required minLength="6" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Confirm password</label>
            <input data-testid="team-accept-confirm" type="password" required minLength="6" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
          <button data-testid="team-accept-submit" type="submit" disabled={busy} className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />} Activate account
          </button>
        </form>
      </div>
    </div>
  );
}

// Batch-3 Item 3: Forgot password — email a 1-hour reset link.
export function TeamForgot() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/pms/team-forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
      toast.success("If that email is registered, a reset link is on its way");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6"><Logo /></div>
        <form data-testid="team-forgot-form" onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 space-y-4">
          <h1 className="font-display font-black text-2xl text-slate-900">Reset your password</h1>
          {sent ? (
            <>
              <p className="text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3">If <b>{email}</b> matches a team account, we've emailed a reset link. It expires in 1 hour. Check your inbox &amp; spam.</p>
              <a href="/team-login" className="block text-center text-[12px] font-semibold text-[#4F46E5] hover:underline">← Back to sign-in</a>
            </>
          ) : (
            <>
              <p className="text-sm text-slate-500">Enter your work email — we'll send you a link to set a new password. Works the same on web &amp; the mobile app.</p>
              <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Work email</label>
                <input data-testid="team-forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
              <button data-testid="team-forgot-submit" type="submit" disabled={busy} className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
                {busy ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />} Send reset link
              </button>
              <a href="/team-login" className="block text-center text-[12px] font-semibold text-slate-500 hover:text-[#4F46E5]">← Back to sign-in</a>
            </>
          )}
        </form>
      </div>
    </div>
  );
}

// Batch-3 Item 3: consume a reset token and set a new password.
export function TeamReset() {
  const { token } = useParams();
  const nav = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error("Password must be at least 6 characters");
    if (password !== confirm) return toast.error("Passwords don't match");
    setBusy(true);
    try {
      const { data } = await api.post("/pms/team-reset-password", { token, password });
      toast.success(`Password updated for ${data.email} — sign in now`);
      nav("/team-login");
    } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="text-center mb-6"><Logo /></div>
        <form data-testid="team-reset-form" onSubmit={submit} className="bg-white rounded-3xl border border-slate-200 shadow-xl p-8 space-y-4">
          <h1 className="font-display font-black text-2xl text-slate-900">Set a new password</h1>
          <p className="text-sm text-slate-500">This link is single-use and expires in 1 hour.</p>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">New password</label>
            <input data-testid="team-reset-password" type="password" required minLength="6" value={password} onChange={(e) => setPassword(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
          <div><label className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Confirm password</label>
            <input data-testid="team-reset-confirm" type="password" required minLength="6" value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1 w-full border border-slate-300 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]/40" /></div>
          <button data-testid="team-reset-submit" type="submit" disabled={busy} className="btn-brand w-full rounded-full py-3 text-sm font-semibold flex items-center justify-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={14} /> : <ShieldCheck size={14} />} Update password
          </button>
        </form>
      </div>
    </div>
  );
}
